import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { MessageCircle, Download, X } from 'lucide-react';
import { toast } from 'sonner';
import { SHOWS } from '../lib/shows';
import { generateResultImage, shareResultImage, downloadResultImage } from '../admin/resultImage';

export function TodayResults() {
  const [images, setImages] = useState({});
  const [dateStr, setDateStr] = useState('');
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data: raw } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/results/latest-day`
        );
        // hold new results until the live-stage spin has fully stopped (~20s)
        const SPIN_HOLD_MS = 22000;
        const data = raw.filter(
          (r) => !r.updated_at || Date.now() - new Date(r.updated_at).getTime() > SPIN_HOLD_MS
        );
        const day = data.length ? data[0].date : '';
        if (cancelled) return;
        setDateStr(day ? day.split('-').reverse().join('.') : '');
        setImages((prev) => (prev.__day === day ? prev : { __day: day }));
        const valid = new Set(
          data.filter((r) => r.letter && r.digits && r.digits.length === 5).map((r) => r.show)
        );
        setImages((prev) => {
          const next = {};
          for (const k of Object.keys(prev)) if (k === '__day' || valid.has(k)) next[k] = prev[k];
          return next;
        });
        for (const r of data) {
          if (cancelled) return;
          if (!r.letter || !r.digits || r.digits.length !== 5) continue;
          const canvas = await generateResultImage(r.show, r.letter, r.digits, r.date);
          if (cancelled) return;
          setImages((prev) =>
            prev[r.show]?.key === r.updated_at
              ? prev
              : {
                  ...prev,
                  [r.show]: { key: r.updated_at, url: canvas.toDataURL('image/jpeg', 0.85), r },
                }
          );
        }
      } catch {
        // retry on next interval
      }
    };
    load();
    const t = setInterval(load, 10000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const published = SHOWS.filter((s) => images[s.id]);
  if (published.length === 0) return null;

  return (
    <section className="px-4 py-16 md:px-12" data-testid="today-results-section">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <p className="mb-2 text-[11px] uppercase tracking-[0.35em] text-[#D4AF37]">
            {dateStr || 'Today'}
          </p>
          <h2 className="font-display text-lg uppercase tracking-[0.2em] text-white md:text-xl">
            Today&apos;s Results
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {published.map((show, i) => (
          <motion.div
            key={show.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08, duration: 0.5 }}
            className="overflow-hidden border border-[#D4AF37]/25 bg-black/40"
            data-testid={`today-result-${show.id}`}
          >
            <div className="flex items-center justify-between border-b border-[#D4AF37]/25 px-4 py-2">
              <span className="font-display text-sm tracking-[0.2em] text-[#F5C542]">
                {show.label} SHOW
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={async () => {
                    const r = images[show.id].r;
                    try {
                      await downloadResultImage(r.show, r.letter, r.digits, r.date);
                      toast.success('Image downloaded');
                    } catch {
                      toast.error('Download failed');
                    }
                  }}
                  data-testid={`today-download-${show.id}`}
                  className="flex items-center gap-1.5 border border-[#F5C542]/50 px-2.5 py-1.5 text-[11px] uppercase tracking-[0.15em] text-[#F5C542] transition-colors hover:bg-[#F5C542] hover:text-black"
                >
                  <Download className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const r = images[show.id].r;
                    try {
                      const res = await shareResultImage(r.show, r.letter, r.digits, r.date, 'whatsapp');
                      if (res === 'fallback') toast.info('Image downloaded — attach it in WhatsApp');
                    } catch {
                      toast.error('Share failed');
                    }
                  }}
                  data-testid={`today-share-${show.id}`}
                  className="flex items-center gap-1.5 border border-green-500/50 px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] text-green-400 transition-colors hover:bg-green-500 hover:text-black"
                >
                  <MessageCircle className="h-3.5 w-3.5" /> Share
                </button>
              </div>
            </div>
            <img
              src={images[show.id].url}
              alt={`Golden Raja ${show.label} result`}
              className="block w-full cursor-zoom-in"
              onClick={() => setViewer(images[show.id].url)}
              data-testid={`today-result-img-${show.id}`}
            />
          </motion.div>
        ))}
      </div>
      {viewer && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 p-2"
          onClick={() => setViewer(null)}
          data-testid="image-fullscreen-viewer"
        >
          <button
            type="button"
            onClick={() => setViewer(null)}
            data-testid="image-viewer-close"
            className="absolute right-3 top-3 z-10 border border-white/30 bg-black/60 p-2 text-white"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={viewer}
            alt="Golden Raja result full view"
            className="max-h-full max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
